import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Get the API URL from environment variables
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
console.log('AuthContext using API URL:', API_URL);

// Define the helper function to construct full API URLs
const createApiUrl = (path) => {
  // Ensure path starts with a slash if API_URL doesn't end with one and path isn't empty
  if (API_URL.endsWith('/') && path.startsWith('/')) {
    return `${API_URL}${path.substring(1)}`;
  }
  if (!API_URL.endsWith('/') && !path.startsWith('/')) {
    return `${API_URL}/${path}`;
  }
  return `${API_URL}${path}`;
};

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [branch, setBranch] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem('token') || null);

  // Setup axios interceptor for authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAuthToken(token);
    }

    // Response interceptor for handling token expiration
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Only try to refresh token once
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Check if refresh token exists
          const refreshToken = localStorage.getItem('refreshToken');
          if (refreshToken && !isRefreshing) {
            try {
              setIsRefreshing(true);
              // Call refresh token endpoint
              const refreshUrl = createApiUrl('/api/auth/refresh-token');
              const response = await axios.post(refreshUrl, { 
                refreshToken 
              }, {
                // Don't send the Authorization header for this request
                headers: { Authorization: '' }
              });
              
              // Update tokens
              const { token } = response.data;
              localStorage.setItem('token', token);
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              setAuthToken(token);
              
              // Retry the original request
              return axios(originalRequest);
            } catch (refreshError) {
              // If refresh token is invalid, log user out
              console.error('Token refresh failed:', refreshError);
              logout();
              return Promise.reject(refreshError);
            } finally {
              setIsRefreshing(false);
            }
          } else {
            // No refresh token available, log user out
            logout();
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      // Clean up interceptor on unmount
      axios.interceptors.response.eject(interceptor);
    };
  }, [isRefreshing]);

  useEffect(() => {
    // Check if the user is already logged in
    checkAuthStatus();
  }, []);

  // Check if user is authenticated
  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const meUrl = createApiUrl('/api/auth/me');
      const response = await axios.get(meUrl);
      
      // Store user data without nesting
      setUser(response.data);
      
      // If user is a company or branch type, fetch the related data
      if (response.data.role === 'company' && response.data.companyId) {
        const companyUrl = createApiUrl(`/api/companies/${response.data.companyId}`);
        const companyResponse = await axios.get(companyUrl);
        setCompany(companyResponse.data);
      } else if (response.data.role === 'branch' && response.data.branchId) {
        const branchUrl = createApiUrl(`/api/branches/${response.data.branchId}`);
        const branchResponse = await axios.get(branchUrl);
        setBranch(branchResponse.data);
        
        // Also fetch the company the branch belongs to
        if (branchResponse.data.companyId) {
          const companyUrl = createApiUrl(`/api/companies/${branchResponse.data.companyId}`);
          const companyResponse = await axios.get(companyUrl);
          setCompany(companyResponse.data);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setUser(null);
      setCompany(null);
      setBranch(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (username, password) => {
    try {
      const loginUrl = createApiUrl('/api/auth/login');
      console.log('Attempting login with URL:', loginUrl);
      
      const response = await axios.post(loginUrl, 
        { username, password },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true
        }
      );
      
      // Save tokens to localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      
      // Set authorization header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      // Store token and user data
      setAuthToken(response.data.token);
      setUser(response.data.user);
      
      // If user is a company or branch type, fetch the related data
      if (response.data.user.role === 'company' && response.data.user.companyId) {
        const companyUrl = createApiUrl(`/api/companies/${response.data.user.companyId}`);
        const companyResponse = await axios.get(companyUrl);
        setCompany(companyResponse.data);
      } else if (response.data.user.role === 'branch' && response.data.user.branchId) {
        const branchUrl = createApiUrl(`/api/branches/${response.data.user.branchId}`);
        const branchResponse = await axios.get(branchUrl);
        setBranch(branchResponse.data);
        
        // Also fetch the company the branch belongs to
        if (branchResponse.data.companyId) {
          const companyUrl = createApiUrl(`/api/companies/${branchResponse.data.companyId}`);
          const companyResponse = await axios.get(companyUrl);
          setCompany(companyResponse.data);
        }
      }
      
      return response.data.user;
    } catch (error) {
      console.error('Login failed:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const logoutUrl = createApiUrl('/api/auth/logout');
      await axios.post(logoutUrl);
    } catch (error) {
      console.error('Logout API failed, continuing local logout:', error);
    } finally {
      // Clear local storage and auth header
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setAuthToken(null);
      setUser(null);
      setCompany(null);
      setBranch(null);
    }
  };

  const value = {
    user,
    company,
    branch,
    isLoading,
    login,
    logout,
    checkAuthStatus,
    authToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
